import { DOCUMENT } from '@angular/common';
import { Injectable, Inject } from '@angular/core';

const INVALID_FIELD_CLASS = 'bw-field-invalid';
const INVALID_LABEL_CLASS = 'bw-field-label-invalid';
const SHAKE_CLASS = 'bw-field-shake';
const INVALID_FORM_CLASS = 'bw-form-has-invalid';
const MANAGED_ARIA_ATTRIBUTE = 'data-bw-managed-aria-invalid';
const REQUIRED_MARKER_ATTRIBUTE = 'data-bw-required-marker';

type FormControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * Provides one consistent response for client-side form validation. Native
 * invalid events cover every standard form; custom validators can mark fields
 * through showInvalidFields without duplicating visual logic.
 */
@Injectable({ providedIn: 'root' })
export class FormFeedbackService {
  private initialized = false;
  private requiredMarkerSyncScheduled = false;
  private requiredMarkerObserver: MutationObserver | null = null;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.document.addEventListener('invalid', this.handleNativeInvalid, true);
    this.document.addEventListener('input', this.handleFormChange, true);
    this.document.addEventListener('change', this.handleFormChange, true);
    this.syncRequiredMarkers();

    const MutationObserverConstructor = this.document.defaultView?.MutationObserver;
    if (MutationObserverConstructor && this.document.body) {
      this.requiredMarkerObserver = new MutationObserverConstructor(() => this.scheduleRequiredMarkerSync());
      this.requiredMarkerObserver.observe(this.document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['required', 'id', 'for']
      });
    }
  }

  showInvalidFields(form: HTMLFormElement, fieldNames: readonly string[]): void {
    this.clearFormState(form);
    const fields = fieldNames
      .flatMap((name) => this.resolveNamedControls(form, name));

    fields.forEach((field) => this.markInvalid(form, field));
    this.focusAndReveal(fields[0]);
  }

  scrollTo(element: HTMLElement | null | undefined): void {
    if (!element) {
      return;
    }

    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  }

  private readonly handleNativeInvalid = (event: Event): void => {
    const field = this.asFormControl(event.target);
    const form = field?.form;
    if (!field || !form) {
      return;
    }

    const shouldReveal = !form.classList.contains(INVALID_FORM_CLASS);
    // Replace the browser tooltip with a repeatable, in-context field cue.
    event.preventDefault();
    this.markInvalid(form, field);
    if (shouldReveal) {
      this.focusAndReveal(field);
    }
  };

  private readonly handleFormChange = (event: Event): void => {
    const field = this.asFormControl(event.target);
    if (field?.form) {
      this.clearFormState(field.form);
    }
  };

  private markInvalid(form: HTMLFormElement, field: FormControlElement): void {
    form.classList.add(INVALID_FORM_CLASS);
    field.classList.remove(SHAKE_CLASS);
    // Force a fresh animation for every submit attempt, including repeated ones.
    void field.offsetWidth;
    field.classList.add(INVALID_FIELD_CLASS, SHAKE_CLASS);
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute(MANAGED_ARIA_ATTRIBUTE, 'true');
    const label = this.findLabel(field, form);
    if (label) {
      label.classList.remove(SHAKE_CLASS);
      void label.offsetWidth;
      label.classList.add(INVALID_LABEL_CLASS, SHAKE_CLASS);
    }
  }

  private clearFormState(form: HTMLFormElement): void {
    form.classList.remove(INVALID_FORM_CLASS);
    form.querySelectorAll<FormControlElement>(`.${INVALID_FIELD_CLASS}`).forEach((field) => {
      field.classList.remove(INVALID_FIELD_CLASS, SHAKE_CLASS);
      if (field.getAttribute(MANAGED_ARIA_ATTRIBUTE) === 'true') {
        field.removeAttribute('aria-invalid');
        field.removeAttribute(MANAGED_ARIA_ATTRIBUTE);
      }
    });
    form.querySelectorAll<HTMLElement>(`.${INVALID_LABEL_CLASS}`).forEach((label) => {
      label.classList.remove(INVALID_LABEL_CLASS, SHAKE_CLASS);
    });
  }

  private resolveNamedControls(form: HTMLFormElement, name: string): FormControlElement[] {
    const controls = form.elements.namedItem(name);
    if (!controls) {
      return [];
    }

    if (controls instanceof RadioNodeList) {
      return Array.from(controls).flatMap((entry) => {
        const field = this.asFormControl(entry);
        return field ? [field] : [];
      });
    }

    const field = this.asFormControl(controls);
    return field ? [field] : [];
  }

  private scheduleRequiredMarkerSync(): void {
    if (this.requiredMarkerSyncScheduled) {
      return;
    }

    this.requiredMarkerSyncScheduled = true;
    queueMicrotask(() => {
      this.requiredMarkerSyncScheduled = false;
      this.syncRequiredMarkers();
    });
  }

  private syncRequiredMarkers(): void {
    this.document.querySelectorAll<FormControlElement>('input[required], select[required], textarea[required]')
      .forEach((field) => {
        if (field instanceof HTMLInputElement && field.type === 'hidden') {
          return;
        }

        const label = this.findLabel(field, field.form);
        if (!label) {
          return;
        }

        const existingMarker = label.querySelector<HTMLElement>(`[${REQUIRED_MARKER_ATTRIBUTE}]`);
        const marker = existingMarker ?? this.document.createElement('span');
        if (!existingMarker) {
          marker.setAttribute(REQUIRED_MARKER_ATTRIBUTE, 'true');
          marker.setAttribute('aria-hidden', 'true');
          marker.className = 'bw-required-marker';
          marker.textContent = ' *';
        }

        // Keep a marker in the same inline wrapper as the label text. A standalone
        // marker becomes a separate grid row when a wrapping label uses display:grid.
        const labelText = label.querySelector<HTMLElement>(
          `:scope > .bw-label-text, :scope > span:not([${REQUIRED_MARKER_ATTRIBUTE}])`
        );
        if (labelText) {
          if (marker.parentElement !== labelText) {
            labelText.append(marker);
          }
          return;
        }

        const directControl = label.querySelector<HTMLElement>(':scope > input, :scope > select, :scope > textarea');
        const childNodes = Array.from(label.childNodes);
        const directControlIndex = directControl ? childNodes.indexOf(directControl) : -1;
        const directTextNodes = childNodes.filter((node, index) =>
          (directControlIndex === -1 || index < directControlIndex)
            && node.nodeType === Node.TEXT_NODE
            && node.textContent?.trim()
        );

        if (directTextNodes.length) {
          const inlineLabelText = this.document.createElement('span');
          inlineLabelText.className = 'bw-label-text';
          label.insertBefore(inlineLabelText, directTextNodes[0]);
          directTextNodes.forEach((node) => inlineLabelText.append(node));
          inlineLabelText.append(marker);
        } else if (directControl) {
          if (marker.parentElement !== label || marker.nextSibling !== directControl) {
            label.insertBefore(marker, directControl);
          }
        } else if (marker.parentElement !== label) {
          label.append(marker);
        }
      });
  }

  private findLabel(field: FormControlElement, form: HTMLFormElement | null): HTMLLabelElement | null {
    const wrappingLabel = field.closest('label');
    if (wrappingLabel instanceof HTMLLabelElement) {
      return wrappingLabel;
    }

    if (!field.id) {
      return field.parentElement?.querySelector('label') ?? null;
    }

    return Array.from((form ?? this.document).querySelectorAll('label')).find((label) => label.htmlFor === field.id)
      ?? field.parentElement?.querySelector('label')
      ?? null;
  }

  private focusAndReveal(field: FormControlElement | undefined): void {
    if (!field) {
      return;
    }

    requestAnimationFrame(() => {
      field.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  }

  private asFormControl(value: EventTarget | Element | null): FormControlElement | null {
    return value instanceof HTMLInputElement || value instanceof HTMLSelectElement || value instanceof HTMLTextAreaElement
      ? value
      : null;
  }
}
